import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Collapse,
  Container,
  Input,
  Row,
  UncontrolledTooltip,
} from "reactstrap";
import { toast } from "react-toastify";
import BreadCrumb from "../../../../Components/Common/BreadCrumb";
import TableContainer from "../../../../Components/Common/TableContainer";
import Loader from "../../../../Components/Common/Loader";
import {
  superadminGetAllCollaborationHistory,
  superadminGetAllCompanyDocuments,
  superadminGetComboCompany,
} from "helpers/backend_helpers";
import { formatDateTime } from "helpers/globalFunction";
import SearchableSelect from "Components/Common/SearchableSelect";
import HistoryCollaborationModalAdd from "./HistoryCollaborationModalAdd";
import HistoryCollaborationModalUpdate from "./HistoryCollaborationModalUpdate";
import HistoryCollaborationModalPreview from "./HistoryCollaborationModalPreview";
import HistoryCollaborationDelete from "./HistoryCollaborationDelete";

const HistoryCollaboration = () => {
  const [tableData, setTableData] = useState<any>([]);
  const [data, setData] = useState<any>({});
  const [sort, setSort] = useState("createTime");
  const [dir, setDir] = useState(-1);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: 10,
  });
  const [rowCount, setRowCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [addModal, setAddModal] = useState<boolean>(false);
  const [updateModal, setUpdateModal] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [previewModal, setPreviewModal] = useState<boolean>(false);
  const [showFilter, setShowFilter] = useState<boolean>(true);
  const [filterName, setFilterName] = useState<string>("");
  const [searchName, setSearchName] = useState<string>("");
  const [filterCompany, setFilterCompany] = useState<any>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setSearchName(filterName);
    }, 500);
    return () => clearTimeout(timer);
  }, [filterName]);

  const getData = () => {
    if (!filterCompany) {
      setTableData([]);
      return;
    }

    setLoading(true);
    const query: any = {
      page: pagination.pageIndex,
      size: pagination.pageSize,
      sort: sort,
      dir: dir,
      companyId: filterCompany.value,
    };

    if (searchName) {
      query.projectName = searchName;
    }

    superadminGetAllCollaborationHistory(query)
      .then((response: any) => {
        setTableData(
          response.content.map((el: any, index: any) => {
            const dibuat = formatDateTime(el.createTime);
            const diperbarui = formatDateTime(el.updateTime);
            return {
              ...el,
              index: index + pagination.pageIndex * pagination.pageSize + 1,
              dibuat,
              diperbarui,
            };
          }),
        );
        setRowCount(response.totalElements);
        setTotalPages(response.totalPages);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
      });
  };

  const searchCompany = async (name: string) => {
    const query = { name };
    return await superadminGetComboCompany(query);
  };

  const formatCompanyOption = (company: any) => ({
    label: `${company.name}`,
    value: company._id,
  });

  const handleResetFilter = () => {
    setFilterName("");
    setSearchName("");
    setFilterCompany(null);
    setTableData([]);
  };

  const handleToggleFilter = () => {
    if (showFilter) handleResetFilter();
    setShowFilter(!showFilter);
  };

  const columns = useMemo(
    () => [
      {
        header: "#",
        accessorKey: "index",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Nama Proyek",
        accessorKey: "projectName",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Nilai Proyek",
        accessorKey: "projectValue",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Lokasi Proyek",
        accessorKey: "projectLocation",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Tanggal Mulai",
        accessorKey: "startDate",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Tanggal Selesai",
        accessorKey: "endDate",
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: "Aksi",
        cell: (cellProps: any) => {
          const { _id, filePath, fileName } = cellProps.row.original;
          return (
            <div className="hstack gap-2">
              <UncontrolledTooltip target={`preview-${_id}`}>
                Preview Dokumen
              </UncontrolledTooltip>
              <button
                id={`preview-${_id}`}
                className="btn btn-sm btn-soft-info"
                onClick={() => {
                  setPreviewModal(true);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-eye-line align-bottom" />
              </button>

              <UncontrolledTooltip target={`download-${_id}`}>
                Download Dokumen
              </UncontrolledTooltip>
              <a
                id={`download-${_id}`}
                href={filePath}
                download={fileName}
                target="_blank"
                rel="noreferrer"
                className="btn btn-sm btn-soft-primary"
              >
                <i className="ri-download-2-line align-bottom" />
              </a>

              <UncontrolledTooltip target={`edit-${_id}`}>
                Edit
              </UncontrolledTooltip>
              <button
                id={`edit-${_id}`}
                className="btn btn-sm btn-soft-warning edit-list"
                onClick={() => {
                  setUpdateModal(true);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-pencil-fill align-bottom" />
              </button>

              <UncontrolledTooltip target={`delete-${_id}`}>
                Hapus
              </UncontrolledTooltip>
              <button
                id={`delete-${_id}`}
                className="btn btn-sm btn-soft-danger remove-list"
                onClick={() => {
                  setDeleteModal(true);
                  setData(cellProps.row.original);
                }}
              >
                <i className="ri-delete-bin-5-fill align-bottom" />
              </button>
            </div>
          );
        },
      },
    ],
    [],
  );

  useEffect(() => {
    getData();
  }, [pagination, sort, dir, searchName, filterCompany]);

  document.title = "Riwayat Kolaborasi";

  return (
    <React.Fragment>
      <div className="page-content">
        {/* 

         */}
        <HistoryCollaborationModalAdd
          show={addModal}
          onCloseClick={() => setAddModal(false)}
          getData={() => getData()}
          company={filterCompany}
        />

        <HistoryCollaborationModalUpdate
          show={updateModal}
          onCloseClick={() => setUpdateModal(false)}
          getData={() => getData()}
          data={data}
        />

        <HistoryCollaborationModalPreview
          show={previewModal}
          onCloseClick={() => {
            setPreviewModal(false);
            setData({});
          }}
          data={data}
        />

        <HistoryCollaborationDelete
          show={deleteModal}
          onCloseClick={() => setDeleteModal(false)}
          getData={() => getData()}
          data={data}
          setData={setData}
        />

        <Container fluid>
          <BreadCrumb
            title="Riwayat Kolaborasi"
            pageTitle="Riwayat Kolaborasi"
          />
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className="border-0">
                  <div className="d-flex align-items-center">
                    <h5 className="card-title mb-0 flex-grow-1">
                      Riwayat Kolaborasi
                    </h5>
                    <div className="flex-shrink-0">
                      <div className="d-flex gap-2 flex-wrap">
                        {/* <button
                          className="btn btn-soft-primary btn-sm"
                          onClick={handleToggleFilter}
                        >
                          <i className="ri-filter-3-line align-bottom me-1"></i>
                          Filters
                        </button> */}

                        <button
                          className={`btn btn-sm ${
                            filterCompany
                              ? "btn-soft-success"
                              : "btn-soft-secondary"
                          }`}
                          disabled={!filterCompany}
                          onClick={() => setAddModal(true)}
                        >
                          <i className="ri-add-line align-bottom me-1"></i>
                          Tambah
                        </button>
                      </div>
                    </div>
                  </div>
                </CardHeader>
                <CardBody className="pt-0">
                  <Row className="g-3 mb-4">
                    <Col lg={4}>
                      <label className="form-label text-muted small mb-1">
                        Perusahaan <span className="text-danger">*</span>
                      </label>
                      <SearchableSelect
                        value={filterCompany}
                        onChange={setFilterCompany}
                        onSearch={searchCompany}
                        formatOptionLabel={formatCompanyOption}
                        placeholder="Cari dan pilih perusahaan..."
                        minSearchLength={2}
                        itemsPerPage={5}
                        isClearable
                      />
                    </Col>
                    <Col lg={4}>
                      <label className="form-label text-muted small mb-1">
                        Nama Proyek
                      </label>
                      <Input
                        type="text"
                        className="form-control"
                        placeholder="Cari nama proyek..."
                        value={filterName}
                        onChange={(e) => setFilterName(e.target.value)}
                        disabled={!filterCompany}
                      />
                    </Col>
                    <Col lg={12}>
                      <button
                        className="btn btn-soft-secondary btn-sm"
                        onClick={handleResetFilter}
                      >
                        <i className="ri-refresh-line align-bottom me-1"></i>
                        Reset
                      </button>
                    </Col>
                  </Row>

                  {!filterCompany ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i
                          className="ri-building-2-line text-muted"
                          style={{ fontSize: "3rem" }}
                        ></i>
                      </div>
                      <h6 className="text-muted fw-semibold">
                        Pilih Perusahaan Terlebih Dahulu
                      </h6>
                      <p className="text-muted small mb-0">
                        Gunakan filter di atas untuk memilih perusahaan dan
                        menampilkan dokumen
                      </p>
                    </div>
                  ) : loading ? (
                    <Loader />
                  ) : tableData.length === 0 ? (
                    <div className="text-center py-5">
                      <div className="mb-3">
                        <i
                          className="ri-file-list-3-line text-muted"
                          style={{ fontSize: "3rem" }}
                        ></i>
                      </div>
                      <h6 className="text-muted fw-semibold">
                        Tidak Ada Dokumen
                      </h6>
                      <p className="text-muted small mb-3">
                        Belum ada dokumen untuk perusahaan{" "}
                        <strong>{filterCompany?.label}</strong>
                      </p>
                      {/* <button
                        className="btn btn-soft-success btn-sm"
                        onClick={() => setAddModal(true)}
                      >
                        <i className="ri-add-line align-bottom me-1"></i>
                        Tambah Dokumen
                      </button> */}
                    </div>
                  ) : (
                    <TableContainer
                      columns={columns}
                      data={tableData}
                      customPageSize={10}
                      theadClass="text-muted text-uppercase"
                      manualPagination
                      pagination={pagination}
                      pageCount={totalPages}
                      onPaginationChange={setPagination}
                    />
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
};

export default HistoryCollaboration;
